import type {
  TransferPart,
  PushSubscriptionRequest,
  ClaimKeyPackagesResponse,
  CommitRequest,
  CommitResponse,
  ConversationInfo,
  CreateInviteRequest,
  ErrorBody,
  GroupInfoResponse,
  InstanceInfo,
  CallTokenResponse,
  RingMode,
  InviteInfo,
  InvitePreview,
  LibraryInfo,
  LibraryEntry,
  CreateLibraryRequest,
  BlobRole,
  BlobUpload,
  BlobUrl,
  LibraryBlob,
  RepoRef,
  RefProtection,
  Pull,
  PullActivity,
  PullDetail,
  PullState,
  ServerFonts,
  Report,
  ReportRequest,
  LibraryReportRequest,
  ModerationKind,
  ServerSettings,
  UpdateStatus,
  StorageLocation,
  StorageOverview,
  MyStorage,
  LocationPolicy,
  LibraryStorageRequest,
  AddStorageLocation,
  AdminUserUpdate,
  SpaceRole,
  SpaceRoleRequest,
  LocalUser,
  UploadStatus,
  LinkPreview,
  LinkTokenResponse,
  NodeOpenTo,
  NodePairing,
  MyDevice,
  LogResponse,
  MeResponse,
  RegisterRequest,
  RegisterResponse,
  Role,
  SpaceInfo,
  SyncResponse,
  UploadKeyPackagesRequest,
  UserId,
  UserProfile,
  Uuid,
  WelcomeEntry,
} from './types'

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly currentEpoch?: number,
  ) {
    super(message || code)
    this.name = 'ApiError'
  }

  is(code: string): boolean {
    return this.code === code
  }
}

/** Human-friendly message for an error from any layer. */
export function describeError(e: unknown): string {
  if (e instanceof ApiError) {
    switch (e.code) {
      case 'invite_invalid':
        return 'This invite link isn’t valid. Ask your friend for a new one.'
      case 'invite_expired':
        return 'This invite link has expired. Ask your friend for a new one.'
      case 'invite_exhausted':
        return 'This invite link has already been used. Ask your friend for a new one.'
      case 'username_taken':
        return 'That username is taken — try another.'
      case 'link_invalid':
        return 'This device link has expired or was already used. Create a new one on your other device.'
      case 'recovery_failed':
        return 'That account and recovery code don’t match.'
      case 'rate_limited':
        return 'Too many attempts. Wait a moment and try again.'
      case 'transport':
        return 'Can’t reach the server. Check your connection.'
      default:
        return e.message
    }
  }
  if (e instanceof Error) return e.message
  return String(e)
}

const API_PREFIX = '/api/v1'

/** Typed client for the hrth HTTP API. `baseUrl` is '' in the browser (same origin). */
export class Api {
  token: string | null = null

  constructor(readonly baseUrl: string = '') {}

  /** URLs the server hands out are often its own paths (`/api/v1/…`): absolute for the apps. */
  private abs = <T extends { url: string }>(r: T): T => (r.url.startsWith('/') ? { ...r, url: `${this.baseUrl}${r.url}` } : r)

  private async request<T>(method: string, path: string, body?: unknown): Promise<T> {
    const headers: Record<string, string> = {}
    if (this.token) headers['Authorization'] = `Bearer ${this.token}`
    if (body !== undefined) headers['Content-Type'] = 'application/json'
    let resp: Response
    try {
      resp = await fetch(`${this.baseUrl}${API_PREFIX}${path}`, {
        method,
        headers,
        body: body === undefined ? undefined : JSON.stringify(body),
      })
    } catch (e) {
      throw new ApiError(0, 'transport', e instanceof Error ? e.message : String(e))
    }
    if (resp.ok) {
      if (resp.status === 204) return undefined as T
      return (await resp.json()) as T
    }
    let err: Partial<ErrorBody> = {}
    try {
      err = (await resp.json()) as ErrorBody
    } catch {
      // non-JSON error body
    }
    throw new ApiError(resp.status, err.code ?? `http_${resp.status}`, err.message ?? resp.statusText, err.current_epoch)
  }

  instance = () => this.request<InstanceInfo>('GET', '/instance')
  register = (req: RegisterRequest) => this.request<RegisterResponse>('POST', '/register', req)
  challenge = (user_id: UserId, device_id: Uuid) =>
    this.request<{ nonce: string }>('POST', '/auth/challenge', { user_id, device_id })
  login = (user_id: UserId, device_id: Uuid, nonce: string, signature: string) =>
    this.request<{ token: string }>('POST', '/auth/login', { user_id, device_id, nonce, signature })
  logout = () => this.request<void>('POST', '/auth/logout')
  me = () => this.request<MeResponse>('GET', '/me')
  updateMe = (display_name: string) => this.request<UserProfile>('PATCH', '/me', { display_name })
  uploadKeyPackages = (req: UploadKeyPackagesRequest) =>
    this.request<{ remaining: number }>('POST', '/keypackages', req)
  claimKeyPackages = (user: UserId) =>
    this.request<ClaimKeyPackagesResponse>('POST', `/users/${encodeURIComponent(user)}/keypackages/claim`)
  createInvite = (req: CreateInviteRequest) => this.request<InviteInfo>('POST', '/invites', req)
  invitePreview = (code: string, host?: string) =>
    this.request<InvitePreview>('GET', `/invites/${encodeURIComponent(code)}${host ? `?host=${encodeURIComponent(host)}` : ''}`)
  /** `host` = the server hosting the invite, when it isn't ours (federation). */
  acceptInvite = (code: string, host?: string) =>
    this.request<{ space_id: Uuid | null }>(
      'POST',
      `/invites/${encodeURIComponent(code)}/accept${host ? `?host=${encodeURIComponent(host)}` : ''}`,
    )
  sync = () => this.request<SyncResponse>('GET', '/sync')
  createSpace = (name: string) => this.request<SpaceInfo>('POST', '/spaces', { name })
  createChannel = (space: Uuid, conversation_id: Uuid, name: string, group_info: string) =>
    this.request<ConversationInfo>('POST', `/spaces/${space}/channels`, { conversation_id, name, group_info })
  leaveSpace = (space: Uuid) => this.request<void>('POST', `/spaces/${space}/leave`)
  /** Reports go to the server hosting the conversation (`host` when it's another one). */
  report = (req: ReportRequest, host?: string | null) =>
    this.request<Report>('POST', `/reports${host ? `?host=${encodeURIComponent(host)}` : ''}`, req)
  reportLibrary = (req: LibraryReportRequest) => this.request<Report>('POST', '/reports/library', req)
  /** A reported library file's ciphertext (admins), to decrypt with the report's key. */
  adminReportFile = async (id: string): Promise<ArrayBuffer> => {
    const resp = await fetch(`${this.baseUrl}${API_PREFIX}/admin/reports/${id}/file`, {
      headers: { Authorization: `Bearer ${this.token}` },
    })
    if (!resp.ok) {
      const e = (await resp.json().catch(() => ({}))) as Partial<ErrorBody>
      throw new ApiError(resp.status, e.code ?? `http_${resp.status}`, e.message ?? resp.statusText)
    }
    return resp.arrayBuffer()
  }
  spaceReports = (space: Uuid) => this.request<{ reports: Report[] }>('GET', `/spaces/${space}/reports`)
  reportAction = (id: string, kind: ModerationKind, minutes?: number) =>
    this.request<Report>('POST', `/reports/${id}/action`, { kind, ...(minutes ? { minutes } : {}) })
  resolveReport = (id: string) => this.request<Report>('POST', `/reports/${id}/resolve`)
  adminReports = () => this.request<{ reports: Report[] }>('GET', '/admin/reports')
  deleteReport = (id: string) => this.request<void>('DELETE', `/admin/reports/${id}`)
  suspendMember = (space: Uuid, user: UserId, minutes: number) =>
    this.request<void>('PUT', `/spaces/${space}/members/${encodeURIComponent(user)}/suspension`, { minutes })
  liftSuspension = (space: Uuid, user: UserId) =>
    this.request<void>('DELETE', `/spaces/${space}/members/${encodeURIComponent(user)}/suspension`)
  kick = (space: Uuid, user: UserId) =>
    this.request<void>('DELETE', `/spaces/${space}/members/${encodeURIComponent(user)}`)
  renameSpace = (space: Uuid, name: string) => this.request<void>('PATCH', `/spaces/${space}`, { name })
  deleteSpace = (space: Uuid) => this.request<void>('DELETE', `/spaces/${space}`)
  renameChannel = (space: Uuid, conv: Uuid, name: string) =>
    this.request<void>('PATCH', `/spaces/${space}/channels/${conv}`, { name })
  deleteChannel = (space: Uuid, conv: Uuid) => this.request<void>('DELETE', `/spaces/${space}/channels/${conv}`)
  setRole = (space: Uuid, user: UserId, role: Role) =>
    this.request<void>('PUT', `/spaces/${space}/members/${encodeURIComponent(user)}/role`, { role })
  createDm = (conversation_id: Uuid, members: UserId[], group_info: string) =>
    this.request<ConversationInfo>('POST', '/dms', { conversation_id, members, group_info })
  leaveDm = (conv: Uuid) => this.request<void>('POST', `/dms/${conv}/leave`)
  addDmMember = (conv: Uuid, user_id: UserId) => this.request<void>('POST', `/dms/${conv}/members`, { user_id })
  removeDmMember = (conv: Uuid, user: UserId) =>
    this.request<void>('DELETE', `/dms/${conv}/members/${encodeURIComponent(user)}`)
  pushSubscribe = (req: PushSubscriptionRequest) => this.request<void>('PUT', '/push/subscription', req)
  pushUnsubscribe = () => this.request<void>('DELETE', '/push/subscription')
  setMuted = (conv: Uuid, muted: boolean) => this.request<void>(muted ? 'PUT' : 'DELETE', `/conversations/${conv}/mute`)
  groupInfo = (conv: Uuid) => this.request<GroupInfoResponse>('GET', `/conversations/${conv}/group-info`)
  commit = (conv: Uuid, req: CommitRequest) =>
    this.request<CommitResponse>('POST', `/conversations/${conv}/commits`, req)
  sendMessage = (conv: Uuid, epoch: number, message: string, commitment?: string, mentions?: UserId[]) =>
    this.request<{ seq: number }>('POST', `/conversations/${conv}/messages`, {
      epoch,
      message,
      ...(commitment ? { commitment } : {}),
      ...(mentions?.length ? { mentions } : {}),
    })
  log = (conv: Uuid, after: number) => this.request<LogResponse>('GET', `/conversations/${conv}/log?after=${after}`)
  myDevices = () => this.request<{ devices: MyDevice[] }>('GET', '/devices')
  removeDevice = (id: Uuid) => this.request<void>('DELETE', `/devices/${id}`)
  createLinkToken = () => this.request<LinkTokenResponse>('POST', '/devices/link-tokens')
  /** One part of a history transfer to another of my devices (HRTH-202); size 0 is complete at once. */
  startTransfer = (to: Uuid, size: number, meta: string) =>
    this.request<UploadStatus>('POST', `/devices/${to}/transfers`, { size, meta })
  transferStatus = (id: string) => this.request<UploadStatus>('GET', `/transfers/${id}`)
  transferPart = (id: string, offset: number, part: Blob) => this.patchPart(`/transfers/${id}`, offset, part)
  completeTransfer = (id: string) => this.request<UploadStatus>('POST', `/transfers/${id}/complete`)
  waitingTransfers = () => this.request<{ parts: TransferPart[] }>('GET', '/me/transfers')
  deleteTransfer = (id: string) => this.request<void>('DELETE', `/transfers/${id}`)
  async transferData(id: string): Promise<ArrayBuffer> {
    let resp: Response
    try {
      resp = await fetch(`${this.baseUrl}${API_PREFIX}/transfers/${id}/data`, { headers: { Authorization: `Bearer ${this.token}` } })
    } catch (e) {
      throw new ApiError(0, 'transport', e instanceof Error ? e.message : String(e))
    }
    if (!resp.ok) {
      const e = (await resp.json().catch(() => ({}))) as Partial<ErrorBody>
      throw new ApiError(resp.status, e.code ?? `http_${resp.status}`, e.message ?? resp.statusText)
    }
    return resp.arrayBuffer()
  }
  linkPreview = (token: string) => this.request<LinkPreview>('GET', `/devices/link/${encodeURIComponent(token)}`)
  linkDevice = (token: string, device_id: Uuid, public_key: string, signature: string) =>
    this.request<RegisterResponse>('POST', '/devices/link', { token, device_id, public_key, signature })
  recoveryStatus = () => this.request<{ enabled: boolean }>('GET', '/recovery')
  setRecovery = (verifier: string) => this.request<void>('PUT', '/recovery', { verifier })
  recover = (user_id: UserId, proof: string, device_id: Uuid, public_key: string, signature: string) =>
    this.request<RegisterResponse>('POST', '/recover', { user_id, proof, device_id, public_key, signature })
  libraries = () => this.request<LibraryInfo[]>('GET', '/libraries')
  /** An encrypted library needs its key wrapped under the space's key. */
  shareLibrary = (name: string, space: Uuid, wrapped?: string) =>
    this.request<void>('PUT', `/libraries/${encodeURIComponent(name)}/shares/${space}`, { wrapped })
  rewrapLibrary = (name: string, space: Uuid, wrapped: string) =>
    this.request<void>('PUT', `/libraries/${encodeURIComponent(name)}/shares/${space}/key`, { wrapped })
  unshareLibrary = (name: string, space: Uuid) =>
    this.request<void>('DELETE', `/libraries/${encodeURIComponent(name)}/shares/${space}`)
  createLibrary = (req: CreateLibraryRequest) => this.request<LibraryInfo>('POST', '/libraries', req)
  libraryEntries = (id: string) =>
    this.request<{ entries: LibraryEntry[] }>('GET', `/libraries/${encodeURIComponent(id)}/entries`)
  addLibraryEntry = (id: string, meta: string) =>
    this.request<LibraryEntry>('POST', `/libraries/${encodeURIComponent(id)}/entries`, { meta })
  updateLibraryEntry = (entry: string, meta: string) =>
    this.request<LibraryEntry>('PATCH', `/library-entries/${encodeURIComponent(entry)}`, { meta })
  deleteLibraryEntry = (entry: string) => this.request<void>('DELETE', `/library-entries/${encodeURIComponent(entry)}`)
  startBlobUpload = (entry: string, role: BlobRole, size: number) =>
    this.request<BlobUpload>('POST', `/library-entries/${encodeURIComponent(entry)}/blobs`, { role, size })
  blobUpload = (id: string) => this.request<BlobUpload>('GET', `/library-uploads/${encodeURIComponent(id)}`)
  blobUploadPart = (id: string, offset: number, part: Blob) =>
    this.patchPart<BlobUpload>(`/library-uploads/${encodeURIComponent(id)}`, offset, part)
  blobPartUrl = async (id: string, n: number) =>
    this.abs(await this.request<{ url: string }>('POST', `/library-uploads/${encodeURIComponent(id)}/parts/${n}/url`)).url
  reportBlobPart = (id: string, n: number, etag: string, size: number) =>
    this.request<BlobUpload>('PUT', `/library-uploads/${encodeURIComponent(id)}/parts/${n}`, { etag, size })
  completeBlobUpload = (id: string) =>
    this.request<LibraryBlob>('POST', `/library-uploads/${encodeURIComponent(id)}/complete`)
  cancelBlobUpload = (id: string) => this.request<void>('DELETE', `/library-uploads/${encodeURIComponent(id)}`)
  repoRefs = (id: string) => this.request<{ refs: RepoRef[] }>('GET', `/libraries/${encodeURIComponent(id)}/refs`)
  refProtections = (id: string) =>
    this.request<{ rules: RefProtection[]; can_manage: boolean }>('GET', `/libraries/${encodeURIComponent(id)}/protections`)
  protectRef = (id: string, refId: string, roles: string[]) =>
    this.request<void>('PUT', `/libraries/${encodeURIComponent(id)}/protections/${encodeURIComponent(refId)}`, { roles })
  unprotectRef = (id: string, refId: string) =>
    this.request<void>('DELETE', `/libraries/${encodeURIComponent(id)}/protections/${encodeURIComponent(refId)}`)
  pulls = (id: string) => this.request<{ pulls: Pull[] }>('GET', `/libraries/${encodeURIComponent(id)}/pulls`)
  openPull = (id: string, body: string) =>
    this.request<PullDetail>('POST', `/libraries/${encodeURIComponent(id)}/pulls`, { body })
  pull = (id: string) => this.request<PullDetail>('GET', `/pulls/${encodeURIComponent(id)}`)
  pullActivity = () => this.request<{ pulls: PullActivity[] }>('GET', '/pulls')
  addPullEvent = (id: string, body: string, state?: PullState) =>
    this.request<PullDetail>('POST', `/pulls/${encodeURIComponent(id)}/events`, { body, ...(state ? { state } : {}) })
  /** Moves refs all together, each only from the version given (409 `ref_conflict` otherwise). */
  updateRefs = (id: string, updates: { id: string; expected: number; target?: string }[]) =>
    this.request<{ refs: RepoRef[] }>('PUT', `/libraries/${encodeURIComponent(id)}/refs`, { updates })
  /** `signed`: for a bucket the server can't reach, a GET signed on this device (it relays it). */
  blobUrl = async (blob: string, signed?: string) =>
    this.abs(await this.request<BlobUrl>('POST', `/library/blobs/${encodeURIComponent(blob)}/url`, signed ? { signed } : undefined))
  /**
   * Sends one request signed on this device through the relay (docs/STORAGE.md §11.2): the
   * bucket's status, ETag and body; the server refusing to relay it throws.
   */
  relay = async (method: string, signed: string, body?: Blob | string): Promise<{ status: number; etag: string | null; text: string }> => {
    let resp: Response
    try {
      resp = await fetch(`${this.baseUrl}${API_PREFIX}/library/relay?method=${method}&url=${encodeURIComponent(signed)}`, {
        method: 'POST',
        headers: this.token ? { Authorization: `Bearer ${this.token}` } : {},
        body,
      })
    } catch (e) {
      throw new ApiError(0, 'transport', e instanceof Error ? e.message : String(e))
    }
    if (!resp.headers.has('x-hrth-relayed')) {
      let err: Partial<ErrorBody> = {}
      try {
        err = (await resp.json()) as ErrorBody
      } catch {
        // non-JSON
      }
      throw new ApiError(resp.status, err.code ?? `http_${resp.status}`, err.message ?? resp.statusText)
    }
    return { status: resp.status, etag: resp.headers.get('ETag'), text: await resp.text() }
  }
  registerMultipart = (id: string, upload_id: string) =>
    this.request<BlobUpload>('PUT', `/library-uploads/${encodeURIComponent(id)}/multipart`, { upload_id })
  setLibraryStorage = (id: string, req: LibraryStorageRequest) =>
    this.request<LibraryInfo>('PUT', `/libraries/${encodeURIComponent(id)}/storage`, req)
  /** An unsealed bucket's details and key, for its owner's device to seal it. */
  myStorageSecret = (id: string) => this.request<AddStorageLocation>('GET', `/me/storage/${encodeURIComponent(id)}/secret`)
  sealMyStorage = (id: string, sealed: string) =>
    this.request<StorageLocation>('POST', `/me/storage/${encodeURIComponent(id)}/seal`, { name: '', kind: 's3', sealed })
  /** `PUT`s a part to a signed bucket URL (no credentials of ours); returns its ETag. */
  putToBucket = async (url: string, body: Blob): Promise<string> => {
    const resp = await fetch(url, { method: 'PUT', body })
    if (!resp.ok) throw new ApiError(resp.status, 'bucket', `the storage refused the upload (${resp.status})`)
    const etag = resp.headers.get('ETag')
    if (!etag) throw new ApiError(0, 'bucket', 'the storage didn’t return an ETag (check its CORS rule exposes ETag)')
    return etag
  }
  renameLibrary = (id: string, name: string) =>
    this.request<LibraryInfo>('PATCH', `/libraries/${encodeURIComponent(id)}`, { name })
  setLibrarySettings = (id: string, settings: string) =>
    this.request<LibraryInfo>('PUT', `/libraries/${encodeURIComponent(id)}/settings`, { settings })
  deleteLibrary = (id: string) => this.request<void>('DELETE', `/libraries/${encodeURIComponent(id)}`)
  grantLibrary = (id: string, user: UserId) =>
    this.request<void>('PUT', `/libraries/${encodeURIComponent(id)}/users/${encodeURIComponent(user)}`)
  revokeLibrary = (id: string, user: UserId) =>
    this.request<void>('DELETE', `/libraries/${encodeURIComponent(id)}/users/${encodeURIComponent(user)}`)
  setSpaceUpload = (id: string, space: Uuid, on: boolean) =>
    this.request<void>(on ? 'PUT' : 'DELETE', `/libraries/${encodeURIComponent(id)}/shares/${space}/upload`)
  setRoleUpload = (id: string, role: string, on: boolean) =>
    this.request<void>(on ? 'PUT' : 'DELETE', `/libraries/${encodeURIComponent(id)}/roles/${encodeURIComponent(role)}/upload`)
  grantLibraryUpload = (id: string, user: UserId) =>
    this.request<void>('PUT', `/libraries/${encodeURIComponent(id)}/uploaders/${encodeURIComponent(user)}`)
  revokeLibraryUpload = (id: string, user: UserId) =>
    this.request<void>('DELETE', `/libraries/${encodeURIComponent(id)}/uploaders/${encodeURIComponent(user)}`)
  localUsers = () => this.request<LocalUser[]>('GET', '/admin/users')
  setPixels = (pixels: string | null) => this.request<UserProfile>('PUT', '/me/pixels', { pixels })
  setSpaceIcon = (space: Uuid, icon: string | null) => this.request<void>('PUT', `/spaces/${space}/icon`, { icon })
  setChannelColor = (space: Uuid, conv: Uuid, color: string | null) =>
    this.request<void>('PUT', `/spaces/${space}/channels/${conv}/color`, { color })
  serverSettings = () => this.request<ServerSettings>('GET', '/admin/settings')
  updateStatus = () => this.request<UpdateStatus>('GET', '/admin/update')
  checkUpdate = () => this.request<UpdateStatus>('POST', '/admin/update/check')
  updateServerSettings = (s: ServerSettings) => this.request<ServerSettings>('PUT', '/admin/settings', s)
  /** Uploads a font file for headings or text. */
  setFont = async (role: 'heading' | 'text', file: File): Promise<ServerFonts> => {
    const resp = await fetch(`${this.baseUrl}${API_PREFIX}/admin/fonts/${role}?file=${encodeURIComponent(file.name)}`, {
      method: 'PUT',
      headers: { Authorization: `Bearer ${this.token}` },
      body: file,
    })
    if (!resp.ok) {
      const e = (await resp.json().catch(() => ({}))) as Partial<ErrorBody>
      throw new ApiError(resp.status, e.code ?? `http_${resp.status}`, e.message ?? resp.statusText)
    }
    return (await resp.json()) as ServerFonts
  }
  /** Links a font: the server fetches it once and serves it itself. */
  linkFont = (role: 'heading' | 'text', url: string) => this.request<ServerFonts>('POST', `/admin/fonts/${role}/link`, { url })
  resetFont = (role: 'heading' | 'text') => this.request<ServerFonts>('DELETE', `/admin/fonts/${role}`)
  storage = () => this.request<StorageOverview>('GET', '/admin/storage')
  addStorage = (req: AddStorageLocation) => this.request<StorageLocation>('POST', '/admin/storage', req)
  checkStorage = (id: string) => this.request<void>('POST', `/admin/storage/${encodeURIComponent(id)}/check`)
  removeStorage = (id: string) => this.request<void>('DELETE', `/admin/storage/${encodeURIComponent(id)}`)
  /** My own storage (docs/STORAGE.md §2.5): buckets I attached, when the server allows it. */
  myStorage = () => this.request<MyStorage>('GET', '/me/storage')
  addMyStorage = (req: AddStorageLocation) => this.request<StorageLocation>('POST', '/me/storage', req)
  pairNode = (id: string, sealed: string) => this.request<NodePairing>('POST', '/me/nodes', { id, sealed })
  /** Opens one of my nodes to people (`rules` sealed; null closes it) (HRTH-182). */
  setNodeRules = (location: string, rules: string | null, openTo: NodeOpenTo) =>
    this.request<void>('PUT', `/me/storage/${encodeURIComponent(location)}/rules`, { rules, open_to: openTo })
  /** Asks a node about its room for me; it seals the answer to `pub`. */
  askNode = (location: string, pub: string) =>
    this.request<{ sealed: string }>('POST', `/storage/${encodeURIComponent(location)}/ask`, { public: pub })
  checkMyStorage = (id: string) => this.request<void>('POST', `/me/storage/${encodeURIComponent(id)}/check`)
  removeMyStorage = (id: string) => this.request<void>('DELETE', `/me/storage/${encodeURIComponent(id)}`)
  renameMyStorage = (id: string, name: string) =>
    this.request<StorageLocation>('PATCH', `/me/storage/${encodeURIComponent(id)}`, { name })
  renameStorage = (id: string, name: string) =>
    this.request<StorageLocation>('PATCH', `/admin/storage/${encodeURIComponent(id)}`, { name })
  /** What a server location is for (STORAGE.md §2.6), and per-person rooms in it. */
  setLocationPolicy = (id: string, policy: LocationPolicy) =>
    this.request<StorageLocation>('PUT', `/admin/storage/${encodeURIComponent(id)}/policy`, policy)
  setDataDirPolicy = (policy: LocationPolicy) => this.request<void>('PUT', '/admin/data-dir/policy', policy)
  setAttachmentStorage = (location: string | null) =>
    this.request<void>('PUT', '/admin/storage/attachments', { location })
  updateAccount = (user: UserId, update: AdminUserUpdate) =>
    this.request<void>('PATCH', `/admin/users/${encodeURIComponent(user)}`, update)
  createRole = (space: Uuid, req: SpaceRoleRequest) => this.request<SpaceRole>('POST', `/spaces/${space}/roles`, req)
  updateRole = (space: Uuid, role: string, req: SpaceRoleRequest) =>
    this.request<SpaceRole>('PATCH', `/spaces/${space}/roles/${encodeURIComponent(role)}`, req)
  deleteRole = (space: Uuid, role: string) => this.request<void>('DELETE', `/spaces/${space}/roles/${encodeURIComponent(role)}`)
  /** Every role id of the space, highest first. */
  reorderRoles = (space: Uuid, roles: string[]) => this.request<void>('PUT', `/spaces/${space}/roles/order`, { roles })
  /** Gives a member a role, replacing the one they held. */
  assignRole = (space: Uuid, role: string, user: UserId) =>
    this.request<void>('PUT', `/spaces/${space}/roles/${encodeURIComponent(role)}/members/${encodeURIComponent(user)}`)
  unassignRole = (space: Uuid, role: string, user: UserId) =>
    this.request<void>('DELETE', `/spaces/${space}/roles/${encodeURIComponent(role)}/members/${encodeURIComponent(user)}`)
  shareLibraryWithRole = (id: string, role: string, wrapped?: string) =>
    this.request<void>('PUT', `/libraries/${encodeURIComponent(id)}/roles/${encodeURIComponent(role)}`, { wrapped })
  unshareLibraryWithRole = (id: string, role: string) =>
    this.request<void>('DELETE', `/libraries/${encodeURIComponent(id)}/roles/${encodeURIComponent(role)}`)
  /** People the caller may credit (admins: anyone known; others: people they share a space or chat with). */
  searchUsers = (q: string) => this.request<UserProfile[]>('GET', `/user-search?q=${encodeURIComponent(q)}`)
  callToken = (conv: Uuid) => this.request<CallTokenResponse>('POST', `/conversations/${conv}/call`)
  callParticipants = (conv: Uuid) => this.request<{ participants: UserId[] }>('GET', `/conversations/${conv}/call`)
  partyParticipants = (conv: Uuid) => this.request<{ participants: UserId[] }>('GET', `/conversations/${conv}/party`)
  partyPresence = (conv: Uuid, joined: boolean) =>
    this.request<{ participants: UserId[] }>('POST', `/conversations/${conv}/party/presence`, { joined })
  /** `ring` says how the first join tells the others (ignored once the call is going). */
  callPresence = (conv: Uuid, joined: boolean, ring?: RingMode) =>
    this.request<{ participants: UserId[] }>('POST', `/conversations/${conv}/call/presence`, ring ? { joined, ring } : { joined })
  declineCall = (conv: Uuid) => this.request<void>('POST', `/conversations/${conv}/call/decline`)
  setAvatar = async (image: Blob): Promise<UserProfile> => {
    const resp = await fetch(`${this.baseUrl}${API_PREFIX}/me/avatar`, {
      method: 'PUT',
      headers: { Authorization: `Bearer ${this.token}`, 'Content-Type': image.type },
      body: image,
    })
    if (!resp.ok) {
      const e = (await resp.json().catch(() => ({}))) as Partial<ErrorBody>
      throw new ApiError(resp.status, e.code ?? `http_${resp.status}`, e.message ?? resp.statusText)
    }
    return (await resp.json()) as UserProfile
  }
  startUpload = (conv: Uuid, size: number) =>
    this.request<{ id: string; size: number; received: number }>('POST', `/conversations/${conv}/uploads`, { size })
  uploadStatus = (id: string) => this.request<{ id: string; size: number; received: number }>('GET', `/uploads/${id}`)
  completeUpload = (id: string) => this.request<{ id: Uuid; size: number }>('POST', `/uploads/${id}/complete`)
  cancelUpload = (id: string) => this.request<void>('DELETE', `/uploads/${id}`)
  uploadPart(id: string, offset: number, part: Blob): Promise<UploadStatus> {
    return this.patchPart(`/uploads/${id}`, offset, part)
  }
  private async patchPart<T = UploadStatus>(path: string, offset: number, part: Blob): Promise<T> {
    let resp: Response
    try {
      resp = await fetch(`${this.baseUrl}${API_PREFIX}${path}`, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${this.token}`, 'Content-Type': 'application/octet-stream', 'Upload-Offset': String(offset) },
        body: part,
      })
    } catch (e) {
      throw new ApiError(0, 'transport', e instanceof Error ? e.message : String(e))
    }
    if (!resp.ok) {
      const e = (await resp.json().catch(() => ({}))) as Partial<ErrorBody>
      throw new ApiError(resp.status, e.code ?? `http_${resp.status}`, e.message ?? resp.statusText)
    }
    return (await resp.json()) as T
  }
  clearAvatar = () => this.request<void>('DELETE', '/me/avatar')
  exportAccount = () => this.request<Record<string, unknown>>('GET', '/me/export')
  /** `libraries`/`locations`: my own, which the move removes from this server (their files stay). */
  startMove = (to: UserId) =>
    this.request<{ code: string; expires_at: number; libraries?: number; locations?: number }>('POST', '/me/move', { to })
  importAccount = (from: UserId, code: string) =>
    this.request<{ spaces: Uuid[]; conversations: Uuid[]; elsewhere: string[] }>('POST', '/me/import', { from, code })
  welcomes = () => this.request<{ welcomes: WelcomeEntry[] }>('GET', '/welcomes')
  ackWelcome = (id: number) => this.request<void>('DELETE', `/welcomes/${id}`)
}

/** The host[:port] of an invite URL, or null for a bare code. */
export function inviteHost(urlOrCode: string): string | null {
  const m = urlOrCode.trim().match(/^https?:\/\/([^/]+)\//i)
  return m?.[1]?.toLowerCase() ?? null
}

/** Extracts the code from an invite URL (`…/i/<code>`) or returns the input. */
export function inviteCode(urlOrCode: string): string {
  // Ignore the #fragment (it carries the space key, never the code) and any query.
  const trimmed = urlOrCode.trim().split('#')[0]!.split('?')[0]!
  const m = trimmed.match(/\/i\/([A-Za-z0-9_-]+)\/?$/)
  return m?.[1] ?? trimmed
}
